/* ============================================================
   Soulskart recent-searches memory — additive, no deps.
   Remembers submitted queries (localStorage) and renders a
   "Recent searches" chip row into the search panel. Self-heals
   across the custom panel's re-renders. Keyboard accessible.
   ============================================================ */
(() => {
  if (window.__ssSearch) return;
  window.__ssSearch = true;

  const KEY = 'ss_recent_searches';
  const MAX = 6;

  const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } };
  const write = (a) => { try { localStorage.setItem(KEY, JSON.stringify(a)); } catch (e) {} };
  const add = (term) => {
    term = (term || '').trim();
    if (!term || term.length > 60) return;
    let a = read().filter((t) => t.toLowerCase() !== term.toLowerCase());
    a.unshift(term);
    write(a.slice(0, MAX));
  };
  const clearAll = () => { write([]); document.querySelectorAll('.ss-recents').forEach((n) => n.remove()); };

  // Clear via delegation so it survives the panel cloning its innerHTML.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('.ss-recents__clear');
    if (btn) { e.preventDefault(); clearAll(); }
  });
  const esc = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // --- Capture queries ---------------------------------------------------
  // 1) whenever the search results page loads with ?q=
  try {
    const u = new URL(location.href);
    if (/\/search\b/.test(u.pathname)) {
      const q = u.searchParams.get('q');
      if (q) add(q);
    }
  } catch (e) {}
  // 2) on submit of any search form (captures before navigation)
  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (f && f.matches && f.matches('form[action*="/search"]')) {
      const inp = f.querySelector('input[name="q"], input[type="search"]');
      if (inp) add(inp.value);
    }
  }, true);

  // --- Styles (self-contained) ------------------------------------------
  const style = document.createElement('style');
  style.textContent = [
    '.ss-recents{margin:0;padding:24px 32px 20px;border-bottom:1px solid var(--ss-hairline,rgba(23,22,21,0.08));}',
    '@media (max-width:749px){.ss-recents{padding:18px 20px 16px;}}',
    '.ss-recents__head{display:flex;align-items:baseline;justify-content:space-between;gap:12px;margin:0 0 10px;}',
    '.ss-recents__title{font-family:var(--ss-font-body);font-size:var(--ss-text-xs,0.75rem);font-weight:700;letter-spacing:0.12em;text-transform:uppercase;opacity:0.55;}',
    '.ss-recents__clear{background:none;border:0;padding:2px 4px;font:inherit;font-size:var(--ss-text-xs,0.75rem);font-weight:600;color:var(--ss-ink);opacity:0.6;cursor:pointer;text-decoration:underline;text-underline-offset:3px;}',
    '.ss-recents__clear:hover{opacity:1;color:var(--ss-accent);}',
    '.ss-recents__row{display:flex;flex-wrap:wrap;gap:8px;}',
    '.ss-recent{display:inline-flex;align-items:center;gap:7px;min-height:34px;padding:0 13px;border:1px solid var(--ss-hairline-strong,rgba(23,22,21,0.16));border-radius:var(--ss-radius-pill,999px);background:var(--ss-surface,#fff);color:var(--ss-ink,#171615);font-size:var(--ss-text-sm,0.88rem);font-weight:600;text-decoration:none;transition:border-color var(--ss-dur-2,.35s) var(--ss-ease,ease),color var(--ss-dur-2,.35s) var(--ss-ease,ease);}',
    '.ss-recent:hover{border-color:var(--ss-ink,#171615);}',
    '.ss-recent:focus-visible{outline:none;box-shadow:0 0 0 2px var(--ss-base,#faf6ee),0 0 0 4px var(--ss-accent,#e0403b);}',
    '.ss-recent svg{width:12px;height:12px;opacity:0.45;flex:0 0 auto;}',
  ].join('');
  document.head.appendChild(style);

  const CLOCK = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/><path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const buildBlock = () => {
    const items = read();
    if (!items.length) return null;
    const wrap = document.createElement('div');
    wrap.className = 'ss-recents';
    wrap.setAttribute('data-ss-recents', '1');
    wrap.innerHTML =
      '<div class="ss-recents__head"><span class="ss-recents__title">Recent searches</span>' +
      '<button type="button" class="ss-recents__clear">Clear</button></div>' +
      '<div class="ss-recents__row" role="list">' +
      items.map((t) => '<a class="ss-recent" role="listitem" href="/search?q=' + encodeURIComponent(t) +
        '&options%5Bprefix%5D=last&type=product">' + CLOCK + '<span>' + esc(t) + '</span></a>').join('') +
      '</div>';
    return wrap; // Clear is handled by delegation (see above)
  };

  // The custom panel replaces its innerHTML with a clone of #ss-search-panel-source.
  // So inject into the SOURCE (clones into every future fill) AND into any panels
  // already filled. buildBlock() is listener-free, so cloning is safe.
  const render = () => {
    if (!read().length) return;
    const source = document.getElementById('ss-search-panel-source');
    if (source && !source.querySelector('.ss-recents')) {
      source.insertBefore(buildBlock(), source.firstChild);
    }
    document.querySelectorAll('.ss-pse__focus').forEach((panel) => {
      if (panel.querySelector('.ss-recents')) return;
      panel.insertBefore(buildBlock(), panel.firstChild);
    });
  };

  const init = () => {
    render();
    let raf;
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(render);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
